import { useSelector } from "react-redux";
import {
  FilterHeaderNameMap,
  canSelectMultipleFilters,
  FAVOURITES_VIEW,
  ODDS_VIEW,
  COMBINED_FILTERS_VIEW,
} from "../../../../constants";
import CloseIcon from "../../../../assets/icons/close.svg";
import FilterHeaderButton from "../FilterHeaderButton";
import FilterHeaderWarning from "../FilterHeaderWarning";
import { FavouritesButton } from "../FavouritesButton";
import { Divider } from "../../../../components/Divider";
import styles from "./FilterHeader.module.scss";
import { FavouritesLimitMessage } from "../../../../components/FavouritesLimitMessage";
import { useFilterType } from "../../../../hooks/useFilterType";
import { useMarketSelections } from "../../../../hooks/useMarketSelections";
import { useFilterActions } from "../../../../hooks/useFilterActions";
import { useFavourites } from "../../../../hooks/useFavourites";

const FilterHeader = () => {
  const { prefetchedFilters, storedFavourites } = useSelector(
    (state) => state.filter
  );
  const { activeView, activeFilterView, experiments, loggedIn } = useSelector(
    (state) => state.view
  );
  const { favouritesEnabled } = experiments;

  const {
    filterType,
    isMultipleSelection,
    getSelectionCount,
    hasValues,
    isCombinedFiltersView,
  } = useFilterType({ activeFilterView });
  const {
    emptyFilters,
    marketSelectionsCount,
    renderNumberOfMarketSelections,
  } = useMarketSelections({ isMultipleSelection });
  const { handleShowAllSelections, closeFilter, clearAllSelections } =
    useFilterActions({ activeFilterView, emptyFilters, marketSelectionsCount });
  const {
    showAddToFavourites,
    isFavouritesView,
    hasStoredFavourites,
    shouldFavouriteBeDisabled,
    storeNewFavourite,
    shouldShowFavouritesLimitMessage,
    getShowAllSelectionsDisabledState,
  } = useFavourites({ activeFilterView, loggedIn, favouritesEnabled });

  // If this is a filter where you can make multiple selections and a filter selection has been
  // added to the filter array we render the filter count icon.
  const renderCount =
    isMultipleSelection && !isCombinedFiltersView && filterType.length > 0 ? (
      <div className={styles.filterCount}>
        <span>{getSelectionCount()}</span>
      </div>
    ) : null;

  // Empty State check to render FilterHeaderWarning
  const emptyState =
    isMultipleSelection &&
    hasValues &&
    !marketSelectionsCount.fetching &&
    marketSelectionsCount.count === 0;

  const getClearAllDisabledState = () => {
    if (activeFilterView === ODDS_VIEW) {
      return !prefetchedFilters.odds.minOdds && !prefetchedFilters.odds.maxOdds;
    }

    if (activeFilterView === FAVOURITES_VIEW) {
      return (
        !prefetchedFilters.favourites.markets &&
        !prefetchedFilters.favourites.odds
      );
    }

    if (activeFilterView === COMBINED_FILTERS_VIEW) {
      return isCombinedFiltersEmpty();
    }

    return activeFilterView !== ODDS_VIEW && !filterType.length;
  };

  const isCombinedFiltersEmpty = () => {
    const { date, markets, fixtures, odds } = prefetchedFilters;

    return (
      date.length === 0 &&
      markets.length === 0 &&
      fixtures.today.length === 0 &&
      fixtures.tomorrow.length === 0 &&
      !odds.minOdds &&
      !odds.maxOdds
    );
  };

  return (
    <div
      className={`${styles.filterHeader} ${
        activeFilterView === "date" ? styles["filterHeader--date"] : ""
      }`}
    >
      <div className={styles.filterHeaderContainer}>
        <h3 className={styles.filterName}>
          {FilterHeaderNameMap.get(activeFilterView)}
          {renderCount}
        </h3>
        <button
          type="button"
          aria-label="filter-header-close-button"
          className={styles.filterCloseButton}
          onClick={closeFilter}
        >
          <img src={CloseIcon} alt="Close" />
        </button>
      </div>
      {canSelectMultipleFilters.includes(activeFilterView) ||
      isCombinedFiltersView ||
      isFavouritesView ? (
        <div className={styles["filterHeaderContainer--buttons"]}>
          <FilterHeaderButton
            name="Clear all"
            ariaLabel="filter-header-clear-all"
            onClick={clearAllSelections}
            disabled={getClearAllDisabledState()}
          />
          <FilterHeaderButton
            name={
              activeView === "betShuffle" && activeFilterView !== ""
                ? "Show Shuffle selections"
                : renderNumberOfMarketSelections()
            }
            ariaLabel="filter-header-show-selections"
            onClick={handleShowAllSelections}
            disabled={getShowAllSelectionsDisabledState()}
          />
        </div>
      ) : null}

      {Boolean(showAddToFavourites) && (
        <>
          <Divider />
          <FavouritesButton
            disabled={shouldFavouriteBeDisabled()}
            hasStoredFavourites={hasStoredFavourites}
            storeNewFavourite={storeNewFavourite}
          />
        </>
      )}

      {Boolean(shouldShowFavouritesLimitMessage()) && (
        <FavouritesLimitMessage limit={storedFavourites.length} />
      )}

      {/* We only show the FilterHeaderWarning component on the Date, Fixtures and Markets filters when a
      user tries to filter selections, so we check if the filterType.length is greater than 0, this prevents
      the FilterHeaderWarning from displaying on first render when all selections will be available.
      Also we check for if the marketSelections count is not fetching and the count is equal to 0 to prevent 
      seeing the warning between renders. */}
      {Boolean(emptyState) && (
        <FilterHeaderWarning>
          <p>Try changing your choices to show more selections.</p>
        </FilterHeaderWarning>
      )}
    </div>
  );
};

export default FilterHeader;
